/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient

/**
 * Combination of private/secret keys suitable for both decryption and signing.
 *
 * It contains two cryptographically independent keys to raise security to a maximum.
 * Any converted keys poses a threat while technically possible so we avoid it.
 */
@Serializable
@SerialName("uprv")
class UniversalPrivateKey(
    val signingKey: SigningSecretKey,
    val decryptingKey: DecryptingSecretKey
) : UniversalKey(), DecryptingKey by decryptingKey, SigningKey by signingKey {

    override val keyBytes by lazy { signingKey.keyBytes + decryptingKey.keyBytes }

    @Transient
    override val magic = KeysmagicNumber.defaultUniversalPrivate

    /**
     * Important! Private key combines signing and decrypting keys, but uses
     * it of the decrypting one to be used in keyring.
     */
    @Transient
    override val id: KeyId = decryptingKey.id

    /**
     * Corresponding public key able to verify amd encrypt data created by this
     * private key.
     */
    val publicKey by lazy {
        UniversalPublicKey(signingKey.verifyingKey, decryptingKey.publicKey)
    }

    companion object {
        /**
         * Required seed size for deterministic universal private key generation.
         *
         * It contains independent signing and decrypting seeds.
         */
        val seedLength = SigningSecretKey.seedLength + DecryptingSecretKey.seedLength

        /**
         * Generate 2 new random keys (4 key pairs under the hood) to securely signd and
         * decrypt data.
         */
        fun new() = UniversalPrivateKey(SigningSecretKey.new(), DecryptingSecretKey.new())

        /**
         * Create a deterministic private key from an exact [seedLength]-byte seed.
         */
        fun fromSeed(seed: UByteArray): UniversalPrivateKey {
            require(seed.size == seedLength) { "seed should be $seedLength bytes" }
            val signingSeed = seed.sliceArray(0..<SigningSecretKey.seedLength)
            val decryptingSeed = seed.sliceArray(SigningSecretKey.seedLength..<seedLength)
            return UniversalPrivateKey(
                SigningSecretKey.fromSeed(signingSeed),
                DecryptingSecretKey.fromSeed(decryptingSeed)
            )
        }

        /**
         * Derive an exact [seedLength]-byte seed from arbitrary byte material.
         *
         * Use this only with high-entropy source bytes. It is not a password KDF.
         */
        fun seedFromBytes(source: UByteArray): UByteArray =
            KeySeed.fromBytes(source, seedLength, "UniversalPrivateKey")
    }
}
